import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api';
import type { AdminUserDto } from '../../api';
import { VerificationStatus } from '../../api/generated/boticaAPI.schemas';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Table, type Column } from '../../components/ui/Table';

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function RoleBadges({ roles }: { roles: string[] | null | undefined }) {
  if (!roles || roles.length === 0) {
    return <span style={{ color: 'var(--text-placeholder)', fontSize: 'var(--text-xs)' }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {roles.map((role) => (
        <RoleBadge key={role} role={role} />
      ))}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  if (role === 'Founder') {
    return (
      <Badge
        variant="neutral"
        style={{
          color: 'var(--terra-700)',
          background: 'var(--terra-100)',
        }}
      >
        {role}
      </Badge>
    );
  }
  return <Badge variant="neutral">{role}</Badge>;
}

const CRED_STATUS_LABEL: Record<VerificationStatus, string> = {
  [VerificationStatus.PendingDocuments]: 'Unverified',
  [VerificationStatus.PendingReview]: 'Pending',
  [VerificationStatus.Verified]: 'Verified',
  [VerificationStatus.Rejected]: 'Rejected',
};

const CRED_STATUS_BADGE: Record<VerificationStatus, 'neutral' | 'pending' | 'approved' | 'rejected'> = {
  [VerificationStatus.PendingDocuments]: 'neutral',
  [VerificationStatus.PendingReview]: 'pending',
  [VerificationStatus.Verified]: 'approved',
  [VerificationStatus.Rejected]: 'rejected',
};

const PAGE_SIZE = 25;

/* ─── UsersPage ──────────────────────────────────────────────────────────── */

export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const fetchUsers = (pg = page, q = debouncedSearch) => {
    setLoading(true);
    setError(null);
    adminApi
      .getUsers({ page: pg, pageSize: PAGE_SIZE, search: q || undefined })
      .then((res) => {
        setUsers(res.users ?? []);
        setTotalPages(res.totalPages ?? 1);
        setTotalCount(res.totalCount ?? 0);
      })
      .catch(() => setError('Failed to load users. Try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers(page, debouncedSearch);
  }, [page, debouncedSearch]);

  const columns: Column<AdminUserDto>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <span style={{ fontWeight: 500 }}>
          {r.fullName || <span style={{ color: 'var(--text-placeholder)' }}>No name</span>}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (r) => (
        <span style={{ color: 'var(--text-secondary)' }}>{r.email ?? '—'}</span>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (r) => <RoleBadges roles={r.roles} />,
    },
    {
      key: 'credential',
      header: 'Credential',
      render: (r) => {
        const v = r.credentialStatus ?? VerificationStatus.PendingDocuments;
        return (
          <Badge variant={CRED_STATUS_BADGE[v]}>
            {CRED_STATUS_LABEL[v]}
          </Badge>
        );
      },
    },
    {
      key: 'created',
      header: 'Registered',
      render: (r) => (
        <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {formatDate(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last login',
      render: (r) => (
        <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
          {formatDate(r.lastLoginAt)}
        </span>
      ),
    },
  ];

  const hasSearch = debouncedSearch.trim().length > 0;
  const emptyMessage = hasSearch
    ? 'No results'
    : 'No users yet';
  const emptyDescription = hasSearch
    ? `No users matched "${debouncedSearch}".`
    : 'Registered users will appear here.';

  return (
    <div style={{ maxWidth: 'var(--content-max-width)' }}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-placeholder)',
              pointerEvents: 'none',
            }}
          >
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            style={{ paddingLeft: 30 }}
            aria-label="Search users"
          />
        </div>

        <span
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-tertiary)',
            marginLeft: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {!loading && !error && `${totalCount} user${totalCount !== 1 ? 's' : ''}`}
        </span>

        {error && (
          <Button variant="secondary" size="sm" onClick={() => fetchUsers()}>
            Retry
          </Button>
        )}
      </div>

      {error && (
        <p
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--badge-rejected-text)',
            marginBottom: 12,
          }}
        >
          {error}
        </p>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <Table
        columns={columns}
        rows={users}
        getKey={(r) => r.id ?? ''}
        loading={loading}
        emptyMessage={emptyMessage}
        emptyDescription={emptyDescription}
        onRowClick={(r) => navigate(`/users/${r.id}`, { state: { user: r } })}
      />

      {/* ── Pagination ───────────────────────────────────────────────────── */}
      {!loading && !error && totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
